/* eslint-disable prettier/prettier */
import axios, {
	AxiosInstance,
	AxiosResponse,
	AxiosError,
	AxiosRequestConfig,
} from 'axios';
import { ApiResponse, ApiError } from '../types/index';
import {
	clearStoredCredentials,
	isAuthErrorReason,
} from '../constants/authError.constant';

class ApiService {
	[x: string]: any;
	private client: AxiosInstance;

	constructor() {
		this.client = axios.create({
			baseURL: process.env.REACT_APP_BASE_API_URL,
			// headers: {
			//     'Content-Type': 'application/json',
			// },
		});

		this.setupInterceptors();
	}

	private setupInterceptors() {
		// Request interceptor
		this.client.interceptors.request.use(
			config => {
				const token = localStorage.getItem('token');
				if (token) {
					config.headers.Authorization = `Bearer ${token}`;
				}
				return config;
			},
			error => {
				return Promise.reject(error);
			}
		);

		// Response interceptor
		this.client.interceptors.response.use(
			(response: AxiosResponse<ApiResponse>) => {
				return response;
			},
			(error: AxiosError<ApiError & { reason?: string }>) => {
				const status = error.response?.status;
				console.log("API ERROR:", {
            status,
            url: error.config?.url,
            method: error.config?.method,
            data: error.response?.data,
        });

				if (status === 401 ) {
					// The old handler left the token in place, so PrivateRoute still
					// considered the user signed in and every route bounced back here.
					clearStoredCredentials();

					// Don't fight a redirect that is already in progress.
					const path = window.location.pathname;
					if (path !== '/login' && path !== '/login/error') {
						const reason = error.response?.data?.reason;
						window.location.href = isAuthErrorReason(reason ?? null)
							? `/login/error?reason=${encodeURIComponent(reason as string)}`
							: '/login';
					}
				}

				return Promise.reject(error);
			}
		);
	}

	async get<T>(
		url: string,
		params?: any,
		courseId?: string
	): Promise<ApiResponse<T>> {
		const response = await this.client.get<ApiResponse<T>>(url, { params });
		return response.data;
	}

	async post<T>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<ApiResponse<T>> {
		const response = await this.client.post<ApiResponse<T>>(
			url,
			data,
			config
		);
		return response.data;
	}

	async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
		const response = await this.client.put<ApiResponse<T>>(url, data);
		return response.data;
	}

	async patch<T>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<ApiResponse<T>> {
		const response = await this.client.patch<ApiResponse<T>>(
			url,
			data,
			config
		);

		return response.data;
	}

	async delete<T>(url: string): Promise<ApiResponse<T>> {
		const response = await this.client.delete<ApiResponse<T>>(url);
		return response.data;
	}

	// async patch<T>(
	//     url: string,
	//     data?: any
	// ): Promise<ApiResponse<T>> {
	//     const response = await this.client.patch<ApiResponse<T>>(
	//         url,
	//         data
	//     );

	//     return response.data;
	// }
}

export const apiService = new ApiService();
